import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import { bound } from "@frui.ts/helpers";
import { getValidationMessage } from "@frui.ts/validation";
import { Observer } from "mobx-react-lite";
import React from "react";
import type { FormControlProps } from "react-bootstrap";
import { Form } from "react-bootstrap";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent } from "@frui.ts/views";
import type { NumberRange } from "@edaz/edazkit/NumberRangeInput";
import NumberRangeInput from "@edaz/edazkit/NumberRangeInput";
import localizationProvider from "../localization/localizationProvider";

export interface NumberRangeInputControlProps {
  min?: number;
  max?: number;
  step?: number;
  customPlaceholder?: string;
}

export default class NumberRangeInputControl<TTarget> extends BindingComponent<
  TTarget,
  NumberRangeInputControlProps & FormControlProps & CommonInputProps & IBindingProps<TTarget>
> {
  protected handleValueChanged = (range: NumberRange) => {
    this.setValue(range);
  };

  @bound protected renderInner() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    const { noValidation, errorMessage, disabled, min, max, step, customPlaceholder } = this.inheritedProps;
    const validationError = noValidation !== true && (errorMessage || getValidationMessage(target, property));

    return (
      <>
        <NumberRangeInput
          onChange={this.handleValueChanged}
          value={this.value}
          className={validationError ? "is-invalid" : ""}
          localization={
            customPlaceholder
              ? localizationProvider.numberRangeInputCustomLocalization(customPlaceholder)
              : localizationProvider.numberRangeInputLocalization
          }
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
        {validationError && (
          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
            {validationError}
          </Form.Control.Feedback>
        )}
      </>
    );
  }

  render() {
    return <Observer render={this.renderInner} />;
  }
}
